/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from '@angular/core';

import { Priority } from '@gh/builder-shared-data';
import { DictionaryItem } from '@gh/common-shared';
import { domOffset, fromAnimationFrame$, getStyle, isNotNil, onAnimationFrame$ } from '@gh/core-util';
import { merge$ } from '@gh/rx';
import { distinctUntilChanged$, first$, map$, mapTo$, switchMap$, takeUntil$ } from '@gh/rx/operators';

import { clamp, isEqual, replace } from 'lodash';
import { Subject, Subscription } from 'rxjs';
import { LeadBoardListItem } from '../../..';

import { LeadBoardCommandService } from '../lead-board-command.service';
import { LeadBoardFormState } from '../lead-board-form.service';
import {
  VirtualDndDragEndedEvent,
  VirtualDndDragMovedEvent,
  VirtualDndDragStartedEvent,
  VirtualDndZoneEvent,
} from '../virtual-dnd/virtual-dnd.service';
import { VirtualPosition, VirtualViewportDirective } from '../virtual-rendering';

type PriorityPanelBound = {
  top: number;
  bottom: number;
  titleHeight: number;
  initialTransform: string;
};

const MOVE_INTERVAL = 150;

const COMMON_SPEED_FACTOR = 5;
const X_SPEED_FACTOR = 2;
const X_SPEED_LEFT_FACTOR = X_SPEED_FACTOR;
const X_SPEED_RIGHT_FACTOR = X_SPEED_FACTOR;
// speed by Y axis should be larger than by X axis
const Y_SPEED_FACTOR = 4;
const Y_SPEED_FACTOR_2 = 3;
const Y_SPEED_TOP_FACTOR = Y_SPEED_FACTOR;
const Y_SPEED_BOTTOM_FACTOR = Y_SPEED_FACTOR * Y_SPEED_FACTOR_2;

function isInMoveInterval(px: number): boolean {
  return px < MOVE_INTERVAL;
}

function toMoveStep(px: number, direction: number): number {
  return (MOVE_INTERVAL - px) * direction / 100 * COMMON_SPEED_FACTOR; // tslint:disable-line:no-magic-numbers
}

function calculateMoveStep(position: number,
                           lowerBound: number,
                           upperBound: number,
                           lowerBoundSpeedFactor: number,
                           upperBoundSpeedFactor: number): number {
  const distanceToLowerBound = position - lowerBound;
  if (isInMoveInterval(distanceToLowerBound)) {
    return toMoveStep(distanceToLowerBound, -1) * lowerBoundSpeedFactor;
  }

  const distanceToUpperBound = upperBound - position;
  if (isInMoveInterval(distanceToUpperBound)) {
    return toMoveStep(distanceToUpperBound, 1) * upperBoundSpeedFactor;
  }
  return 0;
}

function isNoMove(step: VirtualPosition): boolean {
  return step.left === 0 && step.top === 0;
}

@Component({
  selector: 'gh-lead-board-container',
  template: require('./lead-board-container.component.html'),
  styles: [require('./lead-board-container.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LeadBoardContainerComponent implements OnInit, OnDestroy, AfterViewInit {

  @Input() priorities: DictionaryItem<any>[];
  @Input() stages: DictionaryItem<any>[];

  @ViewChild('header', { read: ElementRef, static: false }) headerEl: ElementRef;
  // element for scrollable viewport (viewport contains all cards on the screen)
  @ViewChild(VirtualViewportDirective, { static: false }) bodyViewport: VirtualViewportDirective;
  // list of elements for titles for each priority panel (necessary to modify position of title on scrolling)
  @ViewChildren('priorityTitle', { read: ElementRef }) priorityTitleEls: QueryList<ElementRef>;
  // list of elements for each priority panel (necessary to determine position of each panel)
  @ViewChildren('priorityContent', { read: ElementRef }) priorityContentEls: QueryList<ElementRef>;
  @ViewChild('priorityContainer') priorityContainerRef: ElementRef<HTMLElement>;
  headerWidth: number;
  bodyWidth: number;
  prioritiesVisible: any = {};
  toggleWidth: string = '0px';

  // cached height of "Priorities" title
  private bodyTitleHeight: number;
  // cached bounds of each priority panel
  private priorityBounds: PriorityPanelBound[];
  private dataSubscription: Subscription;
  private cardMoveStart$$ = new Subject<void>();
  private cardMove$$ = new Subject<{ x: number; y: number }>();
  private cardMoveEnd$$ = new Subject<void>();
  private cardMoveSubscription: Subscription;

  constructor(public form: LeadBoardFormState,
              private ngZone: NgZone,
              private commandService: LeadBoardCommandService) {
  }

  ngOnInit(): void {
    this.priorities.forEach((item) => {
      this.prioritiesVisible[item.id] = true;
    });
    this.setToggleContainerWidth();
    // scroll should be updated when opened element is changed and sometimes when data is changed
    this.dataSubscription = merge$(this.form.openedId$, this.form.filter$, this.form.table$).pipe(
      // wait until all changes will be applied to the viewport
      switchMap$(() => this.ngZone.onMicrotaskEmpty.pipe(first$())))
      .subscribe(() => {
       // this.updateBounds();
        this.updateScroll();
      });

    const position$ = this.cardMove$$.pipe(
      // check position of card each animation frame
      onAnimationFrame$,
      // calculate pointer position relative to viewport
      map$(({ x, y }) => {
        const bodyOffset = domOffset(this.bodyViewport.element);

        return {
          left: x - bodyOffset.left,
          top: y - bodyOffset.top,
        };
      }));

    const moveStep$ = position$.pipe(
      // calculate shift by x and y axis
      map$(({ left, top }) => ({
        left: calculateMoveStep(left, 0, this.bodyViewport.width, X_SPEED_LEFT_FACTOR, X_SPEED_RIGHT_FACTOR),
        top: calculateMoveStep(top, 0, this.bodyViewport.height, Y_SPEED_TOP_FACTOR, Y_SPEED_BOTTOM_FACTOR),
      })),
      // animate moving of scroll
      switchMap$((step) =>
        isNoMove(step) ?
          [] :
          this.ngZone.runOutsideAngular(fromAnimationFrame$).pipe(mapTo$(step))),
      map$(({ left: shiftLeft, top: shiftTop }) => {
        const { left, top } = this.form.scrollPosition;
        return {
          left: clamp(left + shiftLeft, 0, this.bodyViewport.maxScrollLeft),
          top: clamp(top + shiftTop, 0, this.bodyViewport.maxScrollTop),
        };
      }),
      distinctUntilChanged$(isEqual));

    this.cardMoveSubscription = this.cardMoveStart$$.pipe(
      switchMap$(() => moveStep$.pipe(takeUntil$(this.cardMoveEnd$$))))
      .subscribe((position) => {
        this.ngZone.run(() => {
          this.commandService.setScrollPosition(position);
        });
      });

  }

  ngOnDestroy(): void {
    this.cardMoveStart$$.complete();
    this.cardMoveEnd$$.complete();
    this.cardMove$$.complete();

    if (this.cardMoveSubscription) {
      this.cardMoveSubscription.unsubscribe();
    }
    if (this.dataSubscription) {
      this.dataSubscription.unsubscribe();
    }
  }

  ngAfterViewInit(): void {
   // this.updateBounds();
    this.updateScroll();
  }

  onScroll(): void {
    this.updateScroll();
  }

  onCardMoveStart(event: VirtualDndDragStartedEvent<LeadBoardListItem>): void {
    const item = event.data;
    event.setPreviewData({
      item,
      expanded: this.form.isOpened(item),
    });
    this.commandService.startDrag(event.data);
    this.cardMoveStart$$.next();
  }

  onCardMove(event: VirtualDndDragMovedEvent): void {
    this.cardMove$$.next(event.pagePosition);
  }

  onCardMoveEnd(event: VirtualDndDragEndedEvent): void {
    this.cardMoveEnd$$.next();
    this.commandService.stopDrag(event.itemData);
    if (event.zoneData) {
      this.commandService.dropItem(event.itemData, event.zoneData);
    }
  }

  onZoneEnter(event: VirtualDndZoneEvent): void {
    this.commandService.enterZone(event.zoneData, event.itemData);
  }

  onZoneExit(event: VirtualDndZoneEvent): void {
    this.commandService.exitZone(event.zoneData, event.itemData);
  }

  clearPriorityTitle({ name }: DictionaryItem<Priority>): string {
    return replace(name, /(\s-\s.*)/ig, ''); // important: "A1 - any-text" => "A1"; spaces around the dash are required
  }

  togglePriority(priority: any): void {
    this.prioritiesVisible[priority.id] = !this.prioritiesVisible[priority.id];
    if (!!this.prioritiesVisible[priority.id]) {
      return;
    }
    setTimeout(() => {
      // tslint:disable-next-line:no-magic-numbers
      this.updateScroll(5);
      // tslint:disable-next-line:no-magic-numbers
    },         100);
  }
  /**
   * Update cached size of each priority panel and corresponding titles.
   * This information is used later to determine position of priority title
   */
  private updateBounds(): void {
    const isInitialized = isNotNil(this.priorityBounds);

    const priorityTitleNativeEls = this.priorityTitleEls.map(({ nativeElement }) => nativeElement);
    this.priorityBounds = this.priorityContentEls.map(({ nativeElement }, i) => ({
      top: nativeElement.offsetTop,
      bottom: nativeElement.offsetTop + nativeElement.clientHeight,
      // if bounds was initialized => take cached height and initial transform
      titleHeight: isInitialized ?
        this.priorityBounds[i].titleHeight
        : priorityTitleNativeEls[i].getBoundingClientRect().height,
      initialTransform: isInitialized ?
        this.priorityBounds[i].initialTransform
        : getStyle(priorityTitleNativeEls[i], 'transform'),
    }));
  }

  /**
   * When scroll occurs we have to update position of each priority title.
   * The idea is to display currently displayed priority on the top of the viewport.
   *
   * Also this method updates scroll position of "Stages" header
   */
  // tslint:disable-next-line:typedef
  private updateScroll(scrollTopFix= 0): void {
    const { left: scrollLeft, top: scrollTop } = this.bodyViewport.viewportSnapshot;
    this.headerEl.nativeElement.scrollLeft = scrollLeft;

    this.priorityTitleEls.forEach(({ nativeElement }, i) => {
      const { top, bottom, titleHeight, initialTransform } = this.priorityBounds[i];
      const lastPosition = bottom - titleHeight - scrollTop;
      const priorityTop = clamp(top - scrollTop, Math.min(this.bodyTitleHeight, lastPosition), lastPosition);
      // tslint:disable-next-line:max-line-length
      nativeElement.style.transform = `translate3d(0, ${priorityTop}px, 0.1px) ${initialTransform === 'none' ? '' : initialTransform}`;
    });

    this.commandService.setScrollPosition({ left: scrollLeft, top: scrollTop + scrollTopFix });
  }

  private setToggleContainerWidth(): void {
    // tslint:disable-next-line:prefer-for-of
    const countOfColumn = this.stages.length;
    // tslint:disable-next-line:no-magic-numbers
    const width =  240 * countOfColumn + 10 * (countOfColumn - 1);
    this.toggleWidth = `${width}px`;
  }
}
